// Audience landing pages — "Are you buying? Building? Renovating?"
// Each curates the toolkits, free guides and next steps for one type of visitor.

export const AUDIENCE_PAGES = {
  buying: {
    question: "Are you buying?",
    title: "Buying Property in NSW",
    intro:
      "Before you fall in love with a property, find out what the planning system knows about it. These resources help you uncover zoning, constraints, nearby development and the home's own history — so you negotiate with facts, not hope.",
    toolkits: ["first-home-roadmap", "agent-questions", "due-diligence", "vacant-land"],
    guides: [
      "before-you-buy-research",
      "section-10-7-certificates",
      "understanding-your-lep",
      "bushfire-bal-ratings",
    ],
    beyond: {
      title: "Already exchanged and planning work?",
      text: "Once you own it, the question becomes what you can do with it — that's the Building and Renovating paths.",
      label: "Explore building & renovating",
      to: "/start/building",
    },
    seo: {
      title: "Buying Property in NSW — Start Here",
      description: "Buying a house, unit or land in NSW? Free guides and practical toolkits for due diligence, agent questions, first-home schemes and vacant land checks.",
    },
  },
  building: {
    question: "Are you building?",
    title: "Building New in NSW",
    intro:
      "New homes, granny flats, pools and secondary dwellings all follow one of three approval pathways — exempt, complying development, or a DA. These resources help you find yours before you spend a dollar on drawings.",
    toolkits: ["starter-pack", "zoning-constraints", "bushfire-planning", "flood-planning"],
    guides: [
      "complying-development-cdc",
      "exempt-development",
      "zoning-101",
      "development-applications-explained",
    ],
    beyond: {
      title: "Consent in hand?",
      text: "Your DA approval comes with pages of conditions. Our roadmap service translates your actual consent into a plain-English action plan.",
      label: "Your DA is approved — what's next?",
      to: "/da-approved",
    },
    seo: {
      title: "Building New in NSW — Start Here",
      description: "Building a new home, granny flat or pool in NSW? Understand exempt, CDC and DA pathways, zoning, bushfire and flood rules before you commit.",
    },
  },
  renovating: {
    question: "Are you renovating?",
    title: "Renovating Your Home",
    intro:
      "Extensions, additions and alterations are where homeowners most often discover — too late — that they needed approval. These resources show you which path your project needs and how to protect your views, drainage and neighbours along the way.",
    toolkits: ["renovation-bible", "swimming-pools", "stormwater-guide", "view-loss"],
    guides: [
      "exempt-development",
      "complying-development-cdc",
      "development-applications-explained",
      "dcps-decoded",
    ],
    beyond: {
      title: "Approval received for your renovation?",
      text: "Conditions of consent can be 40+ pages of legal language. Upload yours and get a personalised roadmap of what happens next.",
      label: "Get your post-approval roadmap",
      to: "/da-approved",
    },
    seo: {
      title: "Renovating in NSW — Start Here",
      description: "Renovating or extending in NSW? Learn which approval path your project needs — exempt, CDC or DA — plus pools, stormwater and view-sharing rules.",
    },
  },
  subdividing: {
    question: "Are you subdividing?",
    title: "Dual Occupancy & Subdivision",
    intro:
      "Turning one lot into two is where planning rules bite hardest — minimum lot sizes, zoning permissibility, frontage, services and contributions. These resources help you test feasibility before you pay for plans.",
    toolkits: ["dual-occupancy", "zoning-constraints", "understanding-das"],
    guides: [
      "zoning-101",
      "understanding-your-lep",
      "development-applications-explained",
      "dcps-decoded",
    ],
    beyond: {
      title: "Subdivision consent approved?",
      text: "Subdivision consents carry their own certificate stages — works certificates, then the final Subdivision Certificate. Our roadmap service walks your conditions in order.",
      label: "Map your subdivision conditions",
      to: "/da-approved",
    },
    seo: {
      title: "Dual Occupancy & Subdivision NSW — Start Here",
      description: "Subdividing or building a dual occupancy in NSW? Feasibility, minimum lot sizes, zoning permissibility and the DA process in plain English.",
    },
  },
  commercial: {
    question: "Developing commercial or industrial?",
    title: "Commercial & Industrial Development",
    intro:
      "Shops, offices, warehouses, industrial yards — non-residential projects carry their own rulebook: employment zones, change-of-use triggers, parking and servicing standards, contributions and construction compliance. These resources are built for business owners, investors and small developers who want council-ready commercial projects.",
    toolkits: ["commercial-da-guide", "change-of-use", "industrial-feasibility"],
    guides: [
      "commercial-industrial-start",
      "development-applications-explained",
      "sepps-state-policies",
      "understanding-your-lep",
    ],
    beyond: {
      title: "Commercial projects rarely fit a template",
      text: "Parking ratios, servicing capacity and permissible uses vary street by street. A short conversation with a planner usually saves weeks of redesign — and Planner Connect is on its way.",
      label: "Planner Connect — coming soon",
      to: "/book",
    },
    seo: {
      title: "Commercial & Industrial Development NSW — Start Here",
      description: "Commercial and industrial development in NSW: employment zones, change of use, fit-out approvals, parking, servicing and the DA pathway in plain English.",
    },
  },
  "developing-land": {
    question: "Are you developing?",
    title: "Developing Land in NSW",
    intro:
      "Feasibility is won or lost before the DA is lodged — FSR, servicing, staging, contributions and the RFIs that stall applications. These resources are built for small developers who want council-ready projects.",
    toolkits: ["dual-occupancy", "understanding-das", "view-loss", "checklists-workbooks"],
    guides: [
      "development-applications-explained",
      "understanding-your-lep",
      "dcps-decoded",
      "sepps-state-policies",
    ],
    beyond: {
      title: "Managing a live consent?",
      text: "Upload your development consent and get a condition-by-condition roadmap — including contributions, bonds and certificate stages.",
      label: "Get your development roadmap",
      to: "/da-approved",
    },
    seo: {
      title: "Developing Land in NSW — Start Here",
      description: "Small-scale development in NSW: feasibility, FSR, DAs and RFIs, contributions and view-sharing — practical tools for mum-and-dad developers.",
    },
  },
};
